import { cancelled, put } from 'redux-saga/effects';
import { Action } from 'redux';
import isFunction from 'lodash/isFunction';
import { BaseApiErrorType } from 'src/models/BaseApi';
import { SafeAnyType } from 'src/utils/safeAny';

/**
 * base generator to run common actions in a saga
 * use this saga when you need to run some logic without checking a response
 *
 * @param logicFunction {Function} - common generator function - http requests, side effects, etc
 * @param errorFunction {Function} - function to be called if logicFunction isn't success
 *        if generator passed - it will be run
 *        if function passed - it will be put
 * @param finallyFunction {Function} - the function which will be called at both success and fail cases of saga
 */
export function* baseSaga(
  logicFunction: () => Generator<SafeAnyType, SafeAnyType, SafeAnyType>,
  errorFunction: (e: BaseApiErrorType, er: SafeAnyType) => Action | Generator, // raw response
  finallyFunction?: () => void
): unknown {
  try {
    yield logicFunction();
  } catch (e: SafeAnyType) {
    // eslint-disable-next-line no-console
    console.error(e);

    const errorFunctionType = errorFunction.constructor.name;

    if (errorFunctionType === 'Function') {
      yield put(errorFunction(e.toString(), e) as Action);
    }
    if (errorFunctionType === 'GeneratorFunction' || typeof errorFunction === 'function') {
      yield errorFunction(e.toString(), e);
    }
  } finally {
    if (yield cancelled()) {
      yield put({ type: 'base saga canceled' });
    }
    if (isFunction(finallyFunction)) {
      yield finallyFunction();
    }
  }
}
